import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "examples/LayoutContainers/PageLayout";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import Footer from "layouts/authentication/components/Footer";
import SkipToContent from "components/SkipToContent";
import usePageTitle from "utils/usePageTitle";
import useAPI from "utils/useApi";
import pageRoutes from "page.routes";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";

// Paletă aliniată cu homepage (Services, HowItWorks, CallToAction)
const NAVY = "#061D36";
const NAVY_MID = "#0f2c4a";
const BLUE_ACCENT = "#1565C0";
const TITLE_COLOR = "#0f2c4a";
const TEXT_COLOR = "#5a6b85";
const TEXT_MUTED = "#67748e";
const ACCENT_YELLOW = "#FFC107";
const INFO_BG = "rgba(21, 101, 192, 0.06)";
const INFO_BORDER = "rgba(21, 101, 192, 0.22)";
const NAVY_GRADIENT = `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`;
const BLUE_GRADIENT = "linear-gradient(135deg, #1565C0, #0d47a1)";

// ─────────────────────────────────────────────
// Reusable demo field components
// ─────────────────────────────────────────────

/** Explicație pentru un câmp din formular, fără listă de opțiuni fixă în ghid. */
function DemoApiField({ label, explanation, required }) {
  return (
    <SoftBox mb={3}>
      <SoftBox mb={0.75} ml={0.5}>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
          {label}
          {required && <span style={{ color: "#e53935", marginLeft: 2 }}>*</span>}
        </SoftTypography>
      </SoftBox>
      <SoftBox display="flex" alignItems="flex-start" gap={0.5}>
        <Icon sx={{ fontSize: "0.85rem !important", color: "#1565C0", mt: "2px", flexShrink: 0 }}>info</Icon>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.65 }}>
          {explanation}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

function DemoField({ label, placeholder, icon, explanation, required, multiline, rows = 3 }) {
  return (
    <SoftBox mb={3}>
      <SoftBox display="flex" alignItems="center" mb={0.75} ml={0.5}>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
          {label}
          {required && <span style={{ color: "#e53935", marginLeft: 2 }}>*</span>}
        </SoftTypography>
      </SoftBox>
      <SoftBox
        display="flex"
        alignItems={multiline ? "flex-start" : "center"}
        sx={{
          border: "1px solid rgba(0,0,0,0.15)",
          borderRadius: "8px",
          p: multiline ? "10px 14px" : "0 14px",
          minHeight: multiline ? `${rows * 26 + 20}px` : "42px",
          bgcolor: "#f8f9fa",
          pointerEvents: "none",
        }}
      >
        {icon && (
          <Icon sx={{ color: "#67748e", mr: 1, fontSize: "1.1rem", mt: multiline ? "2px" : 0, flexShrink: 0 }}>
            {icon}
          </Icon>
        )}
        <SoftTypography variant="body2" sx={{ color: "#adb5bd", fontStyle: "italic", lineHeight: 1.6 }}>
          {placeholder}
        </SoftTypography>
      </SoftBox>
      {explanation && (
        <SoftBox mt={0.75} display="flex" alignItems="flex-start" gap={0.5}>
          <Icon sx={{ fontSize: "0.85rem !important", color: "#1565C0", mt: "2px", flexShrink: 0 }}>info</Icon>
          <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.65 }}>
            {explanation}
          </SoftTypography>
        </SoftBox>
      )}
    </SoftBox>
  );
}

function DemoSelect({ label, placeholder, icon, explanation, required, options = [] }) {
  return (
    <SoftBox mb={3}>
      <SoftBox display="flex" alignItems="center" mb={0.75} ml={0.5}>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
          {label}
          {required && <span style={{ color: "#e53935", marginLeft: 2 }}>*</span>}
        </SoftTypography>
      </SoftBox>
      <SoftBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          border: "1px solid rgba(0,0,0,0.15)",
          borderRadius: "8px",
          p: "0 14px",
          height: "42px",
          bgcolor: "#f8f9fa",
          pointerEvents: "none",
        }}
      >
        <SoftBox display="flex" alignItems="center">
          {icon && <Icon sx={{ color: "#67748e", mr: 1, fontSize: "1.1rem" }}>{icon}</Icon>}
          <SoftTypography variant="body2" sx={{ color: "#adb5bd", fontStyle: "italic" }}>
            {placeholder}
          </SoftTypography>
        </SoftBox>
        <Icon sx={{ color: "#67748e", fontSize: "1.2rem" }}>arrow_drop_down</Icon>
      </SoftBox>
      {options.length > 0 && (
        <SoftBox mt={0.5} pl={1}>
          {options.map((opt, i) => (
            <SoftBox key={i} display="flex" alignItems="center" mb={0.25}>
              <SoftBox
                sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#67748e", mr: 1, flexShrink: 0 }}
              />
              <SoftTypography variant="caption" sx={{ color: "#5a6b85" }}>{opt}</SoftTypography>
            </SoftBox>
          ))}
        </SoftBox>
      )}
      {explanation && (
        <SoftBox mt={0.75} display="flex" alignItems="flex-start" gap={0.5}>
          <Icon sx={{ fontSize: "0.85rem !important", color: "#1565C0", mt: "2px", flexShrink: 0 }}>info</Icon>
          <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.65 }}>
            {explanation}
          </SoftTypography>
        </SoftBox>
      )}
    </SoftBox>
  );
}

function DemoRadio({ label, options, explanation, required }) {
  return (
    <SoftBox mb={3}>
      <SoftBox mb={0.75} ml={0.5}>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
          {label}
          {required && <span style={{ color: "#e53935", marginLeft: 2 }}>*</span>}
        </SoftTypography>
      </SoftBox>
      <SoftBox pl={0.5}>
        {options.map((opt, i) => (
          <SoftBox key={i} display="flex" alignItems="center" mb={0.5}>
            <SoftBox
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                border: "2px solid #67748e",
                mr: 1,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {i === 0 && (
                <SoftBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#0f2c4a" }} />
              )}
            </SoftBox>
            <SoftTypography variant="caption" sx={{ color: "#5a6b85" }}>{opt}</SoftTypography>
          </SoftBox>
        ))}
      </SoftBox>
      {explanation && (
        <SoftBox mt={0.75} display="flex" alignItems="flex-start" gap={0.5}>
          <Icon sx={{ fontSize: "0.85rem !important", color: "#1565C0", mt: "2px", flexShrink: 0 }}>info</Icon>
          <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.65 }}>
            {explanation}
          </SoftTypography>
        </SoftBox>
      )}
    </SoftBox>
  );
}

function DemoCheckbox({ label, explanation, required }) {
  return (
    <SoftBox mb={3} display="flex" alignItems="flex-start">
      <SoftBox
        sx={{
          width: 18,
          height: 18,
          border: "2px solid #0f2c4a",
          borderRadius: "4px",
          mr: 1.5,
          mt: "2px",
          flexShrink: 0,
          bgcolor: "transparent",
        }}
      />
      <SoftBox>
        <SoftTypography variant="body2" sx={{ color: "#0f2c4a" }}>
          {label}
          {required && <span style={{ color: "#e53935", marginLeft: 2 }}>*</span>}
        </SoftTypography>
        {explanation && (
          <SoftBox mt={0.5} display="flex" alignItems="flex-start" gap={0.5}>
            <Icon sx={{ fontSize: "0.85rem !important", color: "#1565C0", mt: "2px", flexShrink: 0 }}>info</Icon>
            <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.65 }}>
              {explanation}
            </SoftTypography>
          </SoftBox>
        )}
      </SoftBox>
    </SoftBox>
  );
}

function DemoFileUpload({ explanation }) {
  return (
    <SoftBox mb={3}>
      <SoftBox mb={0.75} ml={0.5}>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
          Atașamente (dovezi){" "}
          <span style={{ color: "#67748e", fontWeight: 400 }}>(opțional)</span>
        </SoftTypography>
      </SoftBox>
      <SoftBox
        sx={{
          border: "1px dashed rgba(0,0,0,0.2)",
          borderRadius: "8px",
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          bgcolor: "#f8f9fa",
          pointerEvents: "none",
        }}
      >
        <Icon sx={{ fontSize: "2rem !important", color: "#67748e", mb: 1 }}>cloud_upload</Icon>
        <SoftTypography variant="caption" sx={{ color: "#67748e" }}>
          Trageți fișierele aici sau click pentru a încărca
        </SoftTypography>
      </SoftBox>
      {explanation && (
        <SoftBox mt={0.75} display="flex" alignItems="flex-start" gap={0.5}>
          <Icon sx={{ fontSize: "0.85rem !important", color: "#1565C0", mt: "2px", flexShrink: 0 }}>info</Icon>
          <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.65 }}>
            {explanation}
          </SoftTypography>
        </SoftBox>
      )}
    </SoftBox>
  );
}

function SectionHeader({ icon, title, subtitle, color = BLUE_ACCENT }) {
  return (
    <SoftBox mb={4}>
      <SoftBox display="flex" alignItems="center" mb={1}>
        <SoftBox
          sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            background: color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1.5,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: "1.2rem !important" }}>{icon}</Icon>
        </SoftBox>
        <SoftTypography variant="h5" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
          {title}
        </SoftTypography>
      </SoftBox>
      {subtitle && (
        <SoftTypography variant="body2" color="text" lineHeight={1.7} mt={1}>
          {subtitle}
        </SoftTypography>
      )}
      <Divider sx={{ mt: 2, mb: 3 }} />
    </SoftBox>
  );
}

function StepBadge({ number, label }) {
  return (
    <SoftBox display="flex" alignItems="center" mb={3}>
      <SoftBox
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: BLUE_GRADIENT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 1.5,
          flexShrink: 0,
        }}
      >
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#fff", fontSize: "0.7rem" }}>
          {number}
        </SoftTypography>
      </SoftBox>
      <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
        {label}
      </SoftTypography>
    </SoftBox>
  );
}

// ─────────────────────────────────────────────
// Content sections
// ─────────────────────────────────────────────

function ContentDepunere1() {
  return (
    <SoftBox>
      <SectionHeader
        icon="person"
        title="Pasul 1 – Date de identificare"
        subtitle="În bara de pași: «Date de identificare». Titlul din pagină: «Date de identificare ale petentului». Colectează datele necesare identificării și contactării dumneavoastră."
        color="#0f2c4a"
      />

      <DemoField
        label="Nume și prenume"
        placeholder="Introduceți numele complet"
        icon="person"
        required
        explanation="Introduceți numele și prenumele dumneavoastră complet, exact cum apare în actul de identitate. Această informație este obligatorie."
      />
      <DemoField
        label="Adresă de email"
        placeholder="exemplu@email.com"
        icon="email"
        required
        explanation="Adresa de email este esențială – pe aceasta vă vom trimite confirmarea sesizării și codul unic care vă permite să urmăriți statusul ulterior. Folosiți o adresă la care aveți acces regulat."
      />
      <DemoField
        label="Număr de telefon"
        placeholder="07xx xxx xxx"
        icon="phone"
        required
        explanation="Introduceți un număr de telefon valid. Sunt acceptate atât numere românești (07xx) cât și numere internaționale (inclusiv din UK sau alte țări). Nu introduceți litere sau caractere speciale, doar cifre și simboluri specifice (ex: +, -, spații)."
      />
      <DemoField
        label="Data nașterii"
        placeholder="ZZ/LL/AAAA"
        icon="cake"
        required
        explanation="Selectați data nașterii dumneavoastră. Folosiți selectorul de calendar – mai întâi alegeți anul, apoi luna, apoi ziua. Data nu poate fi în viitor și nu poate fi mai veche de 120 de ani."
      />
      <DemoField
        label="Adresă fizică"
        placeholder="Strada, număr, localitate, județ"
        icon="location_on"
        required
        multiline
        rows={3}
        explanation="Introduceți adresa dumneavoastră fizică completă, incluzând strada, numărul, localitatea și județul. Această informație este necesară pentru corespondența oficială."
      />
      <DemoCheckbox
        label="Solicit menținerea datelor mele confidențiale"
        explanation="Dacă bifați această opțiune, datele dumneavoastră personale vor fi tratate cu un nivel sporit de confidențialitate în cadrul procesului de soluționare."
      />
    </SoftBox>
  );
}

function ContentDepunere2({ incidentTypesStatus = "loading", incidentTypeLabels = [] }) {
  const incidentSelectExplanation =
    incidentTypesStatus === "loading"
      ? "Se încarcă lista actuală de tipuri de incident din platformă…"
      : incidentTypeLabels.length > 0
        ? "Deschideți lista din formular și alegeți o singură categorie."
        : "Deschideți lista din formular și alegeți o singură categorie dintre cele afișate acolo.";
  const incidentSelectOptions = incidentTypesStatus === "ready" ? incidentTypeLabels : [];

  return (
    <SoftBox>
      <SectionHeader
        icon="description"
        title="Pasul 2 – Detalii sesizare"
        subtitle="În bara de pași, pasul se numește «Detalii sesizare»; în pagină, titlul este «Detalii despre sesizare». Colectează informațiile despre incident – completați cât mai detaliat posibil."
        color="#0f2c4a"
      />

      <DemoField
        label="Subiect"
        placeholder="Titlul scurt al sesizării"
        icon="title"
        required
        explanation="Un titlu scurt și descriptiv al sesizării. Acesta trebuie să rezume în câteva cuvinte natura incidentului (ex: 'Insultă rasistă la locul de muncă', 'Mesaje antisemite pe rețele sociale')."
      />
      <DemoField
        label="Descriere detaliată"
        placeholder="Vă rugăm să oferiți cât mai multe detalii despre incident"
        icon="description"
        required
        multiline
        rows={6}
        explanation="Descrieți cât mai detaliat incidentul: ce s-a întâmplat, când, unde, cine a fost implicat, ce cuvinte sau acțiuni au fost folosite. Cu cât descrierea este mai completă, cu atât autoritatea competentă poate acționa mai eficient."
      />

      <DemoSelect
        label="Tipul incidentului"
        placeholder="Selectați tipul incidentului"
        icon="category"
        required
        explanation={incidentSelectExplanation}
        options={incidentSelectOptions}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <DemoApiField
            label="Tipul vătămării"
            required
            explanation="Selectați o singură variantă: tipul de prejudiciu pe care îl considerați că l-ați suferit (de exemplu fizic, material, psihic sau că nu a existat o astfel de vătămare), după cum apar opțiunile în formular."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DemoApiField
            label="Pe cine reclamați?"
            required
            explanation="Selectați o singură opțiune din lista afișată în formular pentru cel pe care îl considerați responsabil pentru faptă."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DemoApiField
            label="Loc incident"
            required
            explanation="Precizați dacă fapta s-a petrecut în mediul online sau într-un loc fizic. În funcție de răspuns, mai jos vi se vor cere detalii diferite despre unde anume s-a întâmplat."
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <DemoApiField
            label="Ați mai sesizat anterior această situație?"
            required
            explanation="Indicați dacă ați mai reclamat aceeași situație la o altă autoritate înainte de a folosi acest formular. Se alege o singură variantă dintre cele afișate (de obicei da sau nu)."
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SoftBox mb={3}>
            <SoftBox mb={0.75} ml={0.5}>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
                Dacă ați sesizat anterior, unde ați depus sesizarea?
                <span style={{ color: "#e53935", marginLeft: 2 }}>*</span>
              </SoftTypography>
              <SoftTypography variant="caption" sx={{ color: "#adb5bd", display: "block", mt: 0.25, fontWeight: 400 }}>
                Acest câmp apare doar dacă ați răspuns că ați mai sesizat situația în altă parte.
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" alignItems="flex-start" gap={0.5}>
              <Icon sx={{ fontSize: "0.85rem !important", color: "#1565C0", mt: "2px", flexShrink: 0 }}>info</Icon>
              <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.65 }}>
                Selectați instituția la care ați apelat deja (spre exemplu poliție, parchet, Consiliul Național pentru Combaterea Discriminării, Avocatul Poporului sau o altă autoritate). Dacă nu regăsiți instituția în listă, puteți indica o altă instituție și vi se cere să scrieți numele acesteia. La unele denumiri scurte există o mică pictogramă de informare cu explicații pentru abrevieri.
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </Grid>
        <Grid item xs={12} md={4}>
          <DemoField
            label="Data faptelor"
            placeholder="ZZ/LL/AAAA"
            icon="calendar_today"
            required
            explanation="Data la care s-a produs incidentul. Nu poate fi o dată din viitor."
          />
        </Grid>
      </Grid>

      <SoftBox
        sx={{
          bgcolor: INFO_BG,
          border: `1px solid ${INFO_BORDER}`,
          borderRadius: "8px",
          p: 2,
          mb: 3,
        }}
      >
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a", display: "block", mb: 1 }}>
          <Icon sx={{ fontSize: "0.9rem !important", verticalAlign: "middle", mr: 0.5 }}>info</Icon>
          Unde s-a întâmplat – după ce ați completat data faptelor
        </SoftTypography>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.7, display: "block" }}>
          Dacă ați indicat că fapta s-a petrecut online, vi se cere să precizați unde anume: adresa paginii, rețeaua socială sau alt element care ajută la identificarea locului virtual (puteți include și un link, dacă este cazul).
        </SoftTypography>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.7, display: "block", mt: 1 }}>
          Dacă fapta s-a petrecut în realitate, vi se cere țara. Pentru o adresă în România, completați și județul și localitatea, apoi descrierea locului (stradă, număr, repere utile). Dacă fapta s-a petrecut în străinătate, nu vi se mai cere județ și localitate din România; descrieți locul într-un singur câmp mai amplu (adresă sau explicație în cuvinte).
        </SoftTypography>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.7, display: "block", mt: 1 }}>
          La descrierea locului fizic, puteți adăuga, dacă vă este la îndemână, un link generat din funcția de partajare a locației dintr-o aplicație de hărți, ca să fie mai ușor de găsit punctul pe hartă.
        </SoftTypography>
      </SoftBox>

      <DemoFileUpload explanation="Puteți atașa PDF-uri și imagini (de exemplu JPG, PNG și alte formate acceptate de formular), până la 3 MB per fișier și până la 10 fișiere. Nu este obligatoriu să încărcați documente, dar dovezi clare sprijină analiza sesizării." />
    </SoftBox>
  );
}

function ContentDepunere3() {
  return (
    <SoftBox>
      <SectionHeader
        icon="verified_user"
        title="Pasul 3 – Elemente suplimentare"
        subtitle="Veți vedea un scurt mesaj despre confidențialitate, acordul obligatoriu pentru protecția datelor (GDPR) și verificarea captcha. Pentru a merge mai departe folosiți butonul de trimitere a sesizării."
        color="#0f2c4a"
      />

      <SoftBox
        mb={3}
        p={2}
        border="1px solid"
        borderColor="rgba(15, 44, 74, 0.18)"
        borderRadius="8px"
        bgcolor="#f8f9fa"
      >
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.65, display: "flex", alignItems: "flex-start", gap: 0.75 }}>
          <Icon sx={{ fontSize: "1rem !important", color: "#0f2c4a", flexShrink: 0 }}>info</Icon>
          <span>
            Formularul reamintește că informațiile sunt tratate confidențial și folosite doar pentru soluționarea sesizării.
          </span>
        </SoftTypography>
      </SoftBox>

      <DemoCheckbox
        label="Sunt de acord cu prelucrarea datelor mele personale în conformitate cu Regulamentul General privind Protecția Datelor (GDPR)*"
        required
        explanation="Trebuie bifat pentru a putea trimite sesizarea. Este obligatoriu."
      />

      <SoftBox mb={1.5} ml={0.5}>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
          CAPTCHA: Vă rugăm să confirmați că nu sunteți un robot
        </SoftTypography>
      </SoftBox>
      <SoftBox
        sx={{
          border: "1px dashed rgba(0,0,0,0.15)",
          borderRadius: "8px",
          p: 3,
          bgcolor: "#f8f9fa",
          mb: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <SoftBox
          sx={{
            width: 200,
            height: 80,
            bgcolor: "#e9ecef",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SoftTypography variant="h6" sx={{ color: "#67748e" }}>CAPTCHA</SoftTypography>
        </SoftBox>
        <SoftBox mt={1} display="flex" alignItems="flex-start" gap={0.5}>
          <Icon sx={{ fontSize: "0.85rem !important", color: "#1565C0", mt: "2px", flexShrink: 0 }}>info</Icon>
          <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.65 }}>
            Completați verificarea indicată în această zonă; după trimiterea sesizării urmează confirmarea prin cod pe email (vezi mai jos).
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      <SoftBox
        sx={{
          bgcolor: INFO_BG,
          border: `1px solid ${INFO_BORDER}`,
          borderRadius: "8px",
          p: 2,
          mb: 3,
        }}
      >
        <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a", display: "block", mb: 1 }}>
          După trimiterea sesizării – confirmare pe email
        </SoftTypography>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.7, display: "block" }}>
          Vi se cere codul primit pe adresa de email folosită la sesizare. Codul are o valabilitate limitată (aproximativ cinci minute), cu numărătoare inversă vizibilă. Confirmați cu butonul de validare sau solicitați un cod nou dacă a expirat.
        </SoftTypography>
      </SoftBox>

      <SoftBox
        sx={{
          background: "linear-gradient(135deg, #e8f5e9, #f1f8e9)",
          border: "1px solid #a5d6a7",
          borderRadius: "10px",
          p: 3,
          mb: 3,
        }}
      >
        <SoftBox display="flex" alignItems="center" mb={1.5}>
          <Icon sx={{ color: "#2e7d32", mr: 1 }}>check_circle</Icon>
          <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#2e7d32" }}>
            După validarea cu succes
          </SoftTypography>
        </SoftBox>
        <SoftBox component="ul" pl={2}>
          {[
            "Apare un mesaj de succes: sesizarea a fost înregistrată.",
            "Primiți confirmare pe email, la adresa indicată în formular.",
            "Pe ecran vi se arată codul unic al sesizării; îl puteți copia cu un singur click.",
            "Păstrați codul pentru a verifica mai târziu stadiul sesizării; puteți începe o sesizare nouă din același ecran, dacă aveți nevoie.",
          ].map((item, i) => (
            <SoftBox component="li" key={i} mb={0.5}>
              <SoftTypography variant="body2" sx={{ color: "#2e7d32" }}>{item}</SoftTypography>
            </SoftBox>
          ))}
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

function ContentVerificare1() {
  return (
    <SoftBox>
      <SectionHeader
        icon="search"
        title="Pasul 1 – Autentificare pentru verificare status"
        subtitle="Pentru a vedea statusul sesizării, aveți nevoie de codul unic primit pe email și de adresa de email cu care ați depus sesizarea."
        color="#1565C0"
      />

      <DemoField
        label="Codul unic al sesizării"
        placeholder="Introduceți codul unic de sesizare"
        explanation="Codul unic pe care l-ați primit pe email după depunerea sesizării. Fără acest cod nu puteți accesa informațiile despre sesizarea dumneavoastră."
      />
      <DemoField
        label="Adresa de email"
        placeholder="Introduceți adresa de email"
        icon="email"
        explanation="Adresa de email pe care ați folosit-o la depunerea sesizării. Trebuie să fie aceeași adresă introdusă în formular."
      />

      <SoftBox
        sx={{
          bgcolor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          p: 2,
          mb: 3,
        }}
      >
        <SoftBox display="flex" alignItems="center" mb={0.75}>
          <Icon sx={{ fontSize: "0.95rem !important", color: "#67748e", mr: 0.75 }}>schedule</Icon>
          <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
            Trimitere cod de acces
          </SoftTypography>
        </SoftBox>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.5, display: "block" }}>
          După ce apăsați butonul <strong>„Trimite cod de acces"</strong>, veți primi pe email un cod temporar de verificare.
          Codul este valabil <strong>5 minute</strong> (afișat ca numărătoare inversă pe ecran).
        </SoftTypography>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.5, display: "block", mt: 0.5 }}>
          Dacă codul expiră, puteți solicita unul nou repetând procesul.
        </SoftTypography>
      </SoftBox>

      <DemoField
        label="Codul primit pe email"
        placeholder="Introduceți codul primit pe email"
        explanation="Introduceți codul primit pe email. Apoi apăsați butonul «Verifică și accesează sesizarea» pentru a vedea detaliile și statusul sesizării dumneavoastră."
      />

      <SoftBox
        sx={{
          bgcolor: "#f8f9fa",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          p: 2,
          mb: 3,
        }}
      >
        <SoftBox display="flex" alignItems="center" mb={0.75}>
          <Icon sx={{ fontSize: "0.95rem !important", color: "#67748e", mr: 0.75 }}>help_outline</Icon>
          <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
            Ce se întâmplă dacă datele nu se potrivesc?
          </SoftTypography>
        </SoftBox>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.5, display: "block" }}>
          Dacă codul sesizării sau adresa de email sunt incorecte, veți vedea un mesaj de eroare.
          Verificați că ați introdus datele exact cum apar în emailul de confirmare și încercați din nou.
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

function ContentVerificare2() {
  return (
    <SoftBox>
      <SectionHeader
        icon="assignment"
        title="Pasul 2 – Vizualizarea statusului sesizării"
        subtitle="După autentificarea cu succes, veți vedea toate informațiile despre sesizarea dumneavoastră, organizate în mai multe secțiuni."
        color="#1565C0"
      />

      {/* Date generale */}
      <SoftBox
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "10px",
          p: 2.5,
          mb: 3,
          bgcolor: "#fafafa",
        }}
      >
        <SoftBox display="flex" alignItems="center" mb={2}>
          <Icon sx={{ color: "#475569", mr: 1 }}>info</Icon>
          <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
            Date generale ale sesizării
          </SoftTypography>
        </SoftBox>
        <Grid container spacing={2}>
          {[
            { label: "Cod sesizare", desc: "Codul unic al sesizării dumneavoastră, primit pe email." },
            { label: "Data depunerii", desc: "Data și ora la care a fost înregistrată sesizarea." },
            { label: "Instituția care gestionează sesizarea", desc: "Autoritatea competentă căreia i-a fost redirecționată sesizarea." },
            { label: "Număr de înregistrare intern", desc: "Numărul intern de identificare al dosarului." },
          ].map((item, i) => (
            <Grid item xs={12} md={6} key={i}>
              <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#67748e", display: "block" }}>
                  {item.label}
                </SoftTypography>
                <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.4, display: "block", mb: 0 }}>
                  {item.desc}
                </SoftTypography>
              </SoftBox>
            </Grid>
          ))}
        </Grid>
      </SoftBox>

      {/* Status */}
      <SoftBox
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "10px",
          p: 2.5,
          mb: 3,
          bgcolor: "#fafafa",
        }}
      >
        <SoftBox display="flex" alignItems="center" mb={2}>
          <Icon sx={{ color: "#475569", mr: 1 }}>track_changes</Icon>
          <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
            Status actual al sesizării
          </SoftTypography>
        </SoftBox>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.7, display: "block", mb: 1.5 }}>
          Statusul sesizării este afișat sub forma unui badge colorat, indicând stadiul în care se află solicitarea dvs.
        </SoftTypography>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.7, display: "block" }}>
          Pe parcursul procesării sesizării, este posibil să primiți comunicări din partea autorității competente cu privire la stadiul acesteia.
        </SoftTypography>
      </SoftBox>

      {/* Fișiere */}
      <SoftBox
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "10px",
          p: 2.5,
          mb: 3,
          bgcolor: "#fafafa",
        }}
      >
        <SoftBox display="flex" alignItems="center" mb={1.5}>
          <Icon sx={{ color: "#475569", mr: 1 }}>folder</Icon>
          <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
            Fișiere atașate și documente finale
          </SoftTypography>
        </SoftBox>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.7, display: "block" }}>
          Dacă ați atașat fișiere la depunerea sesizării, le veți regăsi listate aici. 
          Fiecare fișier poate fi descărcat individual apăsând butonul <strong>„Descarcă"</strong>.
        </SoftTypography>
        <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.7, display: "block", mt: 0.75 }}>
          Secțiunea <strong>„Documente finale"</strong> conține documentele oficiale emise de instituție 
          (ex: răspunsul oficial, documentul de soluționare), disponibile odată ce sesizarea a fost procesată.
        </SoftTypography>
      </SoftBox>

      {/* Butoane */}
      <SoftBox
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "10px",
          p: 2.5,
          bgcolor: "#fafafa",
        }}
      >
        <SoftBox display="flex" alignItems="center" mb={1.5}>
          <Icon sx={{ color: "#475569", mr: 1 }}>touch_app</Icon>
          <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#0f2c4a" }}>
            Acțiuni disponibile
          </SoftTypography>
        </SoftBox>
        {[
          { icon: "refresh", label: "Reîncarcă statusul", desc: "Actualizează informațiile despre sesizare în timp real." },
        ].map((btn, i) => (
          <SoftBox key={i} display="flex" alignItems="flex-start" mb={1.5}>
            <SoftBox
              sx={{
                width: 30,
                height: 30,
                borderRadius: "6px",
                bgcolor: "#eef1f6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 1.5,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ fontSize: "1rem !important", color: BLUE_ACCENT }}>{btn.icon}</Icon>
            </SoftBox>
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a", display: "block" }}>
                {btn.label}
              </SoftTypography>
              <SoftTypography variant="caption" sx={{ color: "#5a6b85", lineHeight: 1.4, display: "block", mb: 0 }}>
                {btn.desc}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        ))}
      </SoftBox>
    </SoftBox>
  );
}

// ─────────────────────────────────────────────
// Sidebar
// ─────────────────────────────────────────────

const sidebarItems = [
  {
    group: "Flux depunere sesizare",
    icon: "send",
    color: NAVY_MID,
    items: [
      { id: "depunere-1", label: "Pasul 1 – Date de identificare" },
      { id: "depunere-2", label: "Pasul 2 – Detalii sesizare" },
      { id: "depunere-3", label: "Pasul 3 – Elemente suplimentare" },
    ],
  },
  {
    group: "Flux verificare status",
    icon: "search",
    color: BLUE_ACCENT,
    items: [
      { id: "verificare-1", label: "Pasul 1 – Autentificare" },
      { id: "verificare-2", label: "Pasul 2 – Vizualizare status" },
    ],
  },
];

function renderGhidContent(active, incidentTypes) {
  switch (active) {
    case "depunere-1":
      return <ContentDepunere1 />;
    case "depunere-2":
      return (
        <ContentDepunere2
          incidentTypesStatus={incidentTypes.status}
          incidentTypeLabels={incidentTypes.labels}
        />
      );
    case "depunere-3":
      return <ContentDepunere3 />;
    case "verificare-1":
      return <ContentVerificare1 />;
    case "verificare-2":
      return <ContentVerificare2 />;
    default:
      return <ContentDepunere1 />;
  }
}

// ─────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────

function GhidUtilizare() {
  usePageTitle("Ghid de utilizare");
  const [active, setActive] = useState("depunere-1");
  const [incidentTypes, setIncidentTypes] = useState({ status: "loading", labels: [] });
  const navigate = useNavigate();
  const useApi = useAPI();

  useEffect(() => {
    let cancelled = false;
    useApi
      .getComplaintFormData()
      .then((data) => {
        const labels = Array.isArray(data?.incident_types)
          ? data.incident_types
              .map((t) => t?.label || t?.value)
              .filter((s) => s != null && String(s).trim())
          : [];
        if (!cancelled) setIncidentTypes({ status: "ready", labels });
      })
      .catch(() => {
        if (!cancelled) setIncidentTypes({ status: "ready", labels: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <PageLayout>
      <SkipToContent />
      <DefaultNavbar
        routes={pageRoutes}
        transparent
        action={{
          type: "external",
          route: "/formular-sesizare",
          label: "Trimite Sesizare",
          color: "info",
        }}
      />

      {/* Hero banner */}
      <SoftBox
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          outline: "none",
          background: NAVY_GRADIENT,
          pt: { xs: 18, sm: 18, md: 16 },
          pb: { xs: 4, md: 5 },
          px: { xs: 2, md: 6 },
        }}
      >
        <SoftBox maxWidth="900px" mx="auto">
          <SoftBox display="flex" alignItems="center" mb={1.5}>
            <SoftBox
              sx={{ width: 40, height: 2, bgcolor: ACCENT_YELLOW, mr: 1.5, flexShrink: 0 }}
            />
            <SoftTypography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.6)",
                letterSpacing: "3px",
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Stop Rasism – Platformă sesizări
            </SoftTypography>
          </SoftBox>
          <SoftTypography variant="h3" fontWeight="bold" sx={{ color: "#fff", mb: 1.5 }}>
            Ghid de utilizare a platformei
          </SoftTypography>
          <SoftTypography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.8, maxWidth: "650px" }}
          >
            Acest ghid explică pas cu pas cum să completezi formularul de sesizare și cum să verifici 
            statusul sesizării tale, inclusiv rolul fiecărui câmp din formular.
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      {/* Main content */}
      <SoftBox maxWidth="1200px" mx="auto" px={{ xs: 2, md: 4 }} py={{ xs: 4, md: 6 }}>
        <Grid container spacing={4} alignItems="flex-start">
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <SoftBox
              sx={{
                position: { md: "sticky" },
                top: { md: 24 },
                bgcolor: "#fff",
                borderRadius: "12px",
                border: "1px solid #e9ecef",
                overflow: "hidden",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              }}
            >
              {sidebarItems.map((group, gi) => (
                <SoftBox key={gi}>
                  <SoftBox
                    px={2}
                    py={1.5}
                    sx={{ bgcolor: group.color, display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Icon sx={{ color: "#fff", fontSize: "1rem !important" }}>{group.icon}</Icon>
                    <SoftTypography
                      variant="caption"
                      fontWeight="bold"
                      sx={{ color: "#fff", textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.65rem" }}
                    >
                      {group.group}
                    </SoftTypography>
                  </SoftBox>
                  {group.items.map((item) => (
                    <SoftBox
                      key={item.id}
                      px={2}
                      py={1.25}
                      onClick={() => setActive(item.id)}
                      sx={{
                        cursor: "pointer",
                        bgcolor: active === item.id ? `${group.color}12` : "transparent",
                        borderLeft: active === item.id ? `3px solid ${group.color}` : "3px solid transparent",
                        transition: "all 0.2s ease",
                        "&:hover": { bgcolor: `${group.color}08` },
                      }}
                    >
                      <SoftTypography
                        variant="caption"
                        sx={{
                          color: active === item.id ? group.color : "#5a6b85",
                          fontWeight: active === item.id ? 700 : 400,
                          lineHeight: 1.4,
                          display: "block",
                          fontSize: "0.78rem",
                        }}
                      >
                        {item.label}
                      </SoftTypography>
                    </SoftBox>
                  ))}
                  {gi < sidebarItems.length - 1 && <Divider sx={{ m: 0 }} />}
                </SoftBox>
              ))}
            </SoftBox>
          </Grid>

          {/* Content */}
          <Grid item xs={12} md={9}>
            <SoftBox
              sx={{
                bgcolor: "#fff",
                borderRadius: "12px",
                border: "1px solid #e9ecef",
                p: { xs: 2.5, md: 4 },
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                minHeight: "60vh",
              }}
            >
              {renderGhidContent(active, incidentTypes)}
            </SoftBox>

            {/* CTA */}
            <SoftBox
              mt={3}
              sx={{
                background: NAVY_GRADIENT,
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.06)",
                boxShadow: "0 8px 28px rgba(6, 29, 54, 0.22)",
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <SoftBox>
                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: "#fff" }}>
                  Ești gata să depui sesizarea?
                </SoftTypography>
                <SoftTypography variant="caption" sx={{ color: "rgba(255,255,255,0.75)" }}>
                  Accesează formularul și urmează pașii descriși în acest ghid.
                </SoftTypography>
              </SoftBox>
              <SoftButton
                variant="contained"
                onClick={() => navigate("/formular-sesizare")}
                sx={{
                  bgcolor: ACCENT_YELLOW,
                  color: NAVY,
                  fontWeight: 700,
                  "&:hover": { bgcolor: "#FFB300" },
                }}
              >
                <Icon sx={{ mr: 0.75 }}>send</Icon>
                Depune sesizare
              </SoftButton>
            </SoftBox>

            {/* Declarație de accesibilitate */}
            <SoftBox
              mt={3}
              sx={{
                bgcolor: "#f8f9fb",
                border: "1px solid #e9ecef",
                borderRadius: "10px",
                p: 2.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Icon sx={{ color: "#1565C0", fontSize: "1.2rem !important", mt: "2px", flexShrink: 0 }}>
                accessibility_new
              </Icon>
              <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#0f2c4a", display: "block", mb: 0.5, fontSize: "0.78rem" }}>
                  Declarație de accesibilitate
                </SoftTypography>
                <SoftTypography variant="caption" sx={{ color: "#67748e", lineHeight: 1.35, display: "block" }}>
                  Platforma „Stop Rasism" este proiectată în conformitate cu standardele europene de accesibilitate web (WCAG 2.1, nivel AA) aplicabile site-urilor instituțiilor publice.
                </SoftTypography>
                <SoftTypography
                  variant="caption"
                  component="p"
                  sx={{
                    display: "block",
                    mt: 0.75,
                    mb: 0,
                    fontStyle: "italic",
                    color: TEXT_COLOR,
                    lineHeight: 1.55,
                    bgcolor: "rgba(6, 29, 54, 0.06)",
                    py: 1,
                    px: 1.25,
                    borderRadius: "6px",
                  }}
                >
                  Pentru a comunica în limbaj mimico-gestual în dialogul cu autoritățile pot fi utilizate servicii precum „Voci pentru mâini”.
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>

      <Footer />
    </PageLayout>
  );
}

export default GhidUtilizare;

